import { useState, useRef, useCallback, useMemo } from 'react';
import TwinIcon from '../../../../../baseComponents/TwinIcon';
import useOnClickOutside from '../../../../../utils/hooks/useOnClickOut';
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentPropsExtended, OrderStepButtonsHookResult } from './types';
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { getEmployeeParams } from '../../../../../utils/reducers/getters';
import { ModalOpenedTy } from '../../../../../baseComponents/Modal/types';
import { VerticalSortItem } from '../../../../../baseComponents/Sorts/VerticalSortList/types';
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../utils/globals/dictionary';
import { CustomSelectWithSearchBarNotInFilteredItems } from '../../../../../forms/CustomSelect';
import { ModalMedium } from '../../../../../baseComponents/Modal';
import TwinTrans from '../../../../../baseComponents/TwinTrans';
import { ButtonOrLoader } from '../../../../../baseComponents/Button';
import useIsLoading from '../../../../../utils/hooks/useIsLoading';
import { CustomChangeFunction } from '../../../../../forms/Input/types';
import { twinFetchPostJSON } from '../../../../../utils/globals/data';
import { changeEmployeeParams } from '../../../../../utils/reducers/reduxDispatch';
import { VerticalSortListDelete } from '../../../../../baseComponents/Sorts/VerticalSortList';

export const OrderStepButtonsDefaultBaseComponent: React.FC<OrderStepButtonsBaseComponentPropsExtended> = ({hookFunction, className, ...rest }) => {
    const { ButtonComponents } = hookFunction({ ...rest })
    const renderThis: JSX.Element[] = []

    for (const key in ButtonComponents) {
        const element = ButtonComponents[key];
        const componentExtraProps = element?.extraProps;
        const OrderButtonComponent = element?.component;
        const showButton = element?.showButton !== undefined ? element?.showButton : true
        if (showButton) {
            renderThis.push(<OrderButtonComponent {...componentExtraProps} key={key} />)
        }
    }

    return (
        <div className={'flex justify-start flex-wrap items-center gap-15 order_config_buttons ' + (className || '')}>
            {renderThis}
        </div>
    )
}

export const OrderStepButtonsNewStyleBaseComponent: React.FC<OrderStepButtonsBaseComponentPropsExtended> = ({ hookFunction, className, ...rest }) => {
    const { ButtonComponents, name, defaultButtons } = hookFunction({ ...rest})
    const { opened, setOpened, ref, myShowStepButtons, myStepButtons } = useOrderStepButtonsNewStyleBaseComponentLogic({ name, ButtonComponents, defaultButtons })
    const renderThis: JSX.Element[] = []

    for (const key in myShowStepButtons) {
        const element = myShowStepButtons[parseInt(key)]
        const OrderButtonComponent = element?.component;
        const componentExtraProps = element?.extraProps;
        renderThis.push(
            <OrderButtonComponent {...componentExtraProps} key={key} />
        )
    }

    return (
        <div className={'flex relative justify-start flex-wrap items-center gap-x-15 gap-y-10 order_config_buttons ' + (className || '')}>
            {renderThis}
            <div ref={ref}>
                <div className='flex items-center justify-center mb-3 h-28 w-28 cursor-pointer rounded-full bg-green-DB' onClick={() => setOpened((old) => !old)}>
                    <TwinIcon className='text-green-21 regular18' icon={faEllipsisVertical} />
                </div>
                {opened ? <QuoteOrderHiddenButtonsMenu showButtons={myShowStepButtons || []} allStepButtons={myStepButtons} name={name} /> : null}
            </div>
        </div>
    )
}

const useOrderStepButtonsNewStyleBaseComponentLogic = ({ name, ButtonComponents, defaultButtons }: OrderStepButtonsHookResult) => {
    const [opened, setOpened] = useState<boolean>(false)
    const ref = useRef<any>(null)
    const myShowStepButtonsParameter = getEmployeeParams()?.[name]

    const myShowStepButtons = useMemo(() => {
        const result: TwinDictionary[] = []
        const parsedShowStepButtons = JSON.parse(myShowStepButtonsParameter || JSON.stringify(defaultButtons))
        for (const key in parsedShowStepButtons) {
            const element = parsedShowStepButtons[key]
            const button = ButtonComponents[element?.id];
            const showButton = button?.showButton !== undefined ? button?.showButton : true
            if (button && showButton) {
                result.push({...element, ...button })
            }
        }

        return result
    }, [myShowStepButtonsParameter, ButtonComponents, defaultButtons])

    const myStepButtons = useMemo(() => {
        const result: OrderStepButtonsBase = {}
        for (const key in ButtonComponents) {
            const element = ButtonComponents[key];
            const showButton = element?.showButton !== undefined ? element?.showButton : true
            if (showButton) {
                result[key] = element
            }
        }
        return result
    }, [ButtonComponents])

    const onReset = useCallback(() => {
        setOpened(false)
    }, [setOpened])
    useOnClickOutside(ref, onReset)

    return { opened, setOpened, ref, myShowStepButtons, myStepButtons }
}

interface QuoteOrderHiddenButtonsMenuProps {
    showButtons: TwinDictionary[]
    allStepButtons: OrderStepButtonsBase
    name: string
}

const QuoteOrderHiddenButtonsMenu: React.FC<QuoteOrderHiddenButtonsMenuProps> = ({ showButtons, allStepButtons, name }) => {
    const { t } = useTwinTranslation()
    const { opened, setOpened } = useQuoteOrderShowButtonsMenuLogic()
    const renderThis: JSX.Element[] = []

    for (const key in allStepButtons) {
        const element = allStepButtons[key];
        const OrderButtonComponent = element?.component;
        const componentExtraProps = element?.extraProps;
        const parsedShowStepButtons = dictionaryComplexFromJsonArr(showButtons || [])
        const isShowButtonParams = parsedShowStepButtons[componentExtraProps?.id]
        if (!isShowButtonParams) {
            renderThis.push(
                <OrderButtonComponent {...componentExtraProps} key={key}>
                    <div className='cursor-pointer hover:text-green-43 show_step_button_text' key={componentExtraProps?.id}>{componentExtraProps?.title}</div>
                </OrderButtonComponent>
            )
        }
    }

    return (
        <div className='flex flex-col absolute z-5 gap-y-8 bg-white p-15 w-200 rounded-xl q_o_show_step_buttons_menu'>
            {renderThis}
            {renderThis.length ?
                <div className='mt-3'>
                    <div className='border-b border-gray-EE' />
                </div>
                : null
            }
            <div className='hover:text-green-43 cursor-pointer rounded-sm' onClick={() => setOpened(true)}>{t('personalize', 'Personalizar')}</div>
            {opened ?
                <ModalShowStepButtonsConfig myShowStepButtons={showButtons as any} allStepButtons={allStepButtons} setOpened={setOpened} name={name} />
                : null
            }
        </div>
    )
}

const useQuoteOrderShowButtonsMenuLogic = () => {
    const [opened, setOpened] = useState<ModalOpenedTy>(null)
    return { opened, setOpened }
}

interface ModalShowStepButtonsConfigProps extends ModalShowStepButtonsConfigLogicProps { }

const ModalShowStepButtonsConfig: React.FC<ModalShowStepButtonsConfigProps> = ({ allStepButtons, myShowStepButtons, setOpened, name }) => {
    const { t } = useTwinTranslation()
    const { mySelectedStepButtons, onSubmitHandler, excludeIds, changeData, loading, setMySelectedStepButtons, parsedStepButtons } = useModalShowStepButtonsConfigLogic({ myShowStepButtons, setOpened, allStepButtons, name })

    return (
        <ModalMedium opened={true} setOpened={setOpened} className='flex flex-col '>
            <h2 className='mb-35'>
                <TwinTrans transKey='configPersonalizedMenu'>Configura tu menú de acciones</TwinTrans>
            </h2>
            <CustomSelectWithSearchBarNotInFilteredItems excludeItems={excludeIds || []} items={parsedStepButtons} fieldName='title' onChange={changeData} label={t('searchActionTools', 'Buscar acciones...')} className='mb-5' sortItems={true} />
            <div className='modal_negative_margin flex flex-auto flex-col '>
                <div className='overflow-auto flex flex-auto flex-col h-1 pr-10'>
                    <div className='flex-auto table_container mt-20'>
                        <VerticalSortListDelete items={mySelectedStepButtons || []} setItems={setMySelectedStepButtons} />
                    </div>
                    <div className='flex justify-end'>
                        <ButtonOrLoader onClick={onSubmitHandler} buttonIsDisabled={loading} textButton={t('save', 'Guardar')} className='mt-68' />
                    </div>
                </div>
            </div>
        </ModalMedium>
    )
}

interface ModalShowStepButtonsConfigLogicProps {
    myShowStepButtons: VerticalSortItem[]
    allStepButtons: OrderStepButtonsBase
    setOpened: React.Dispatch<React.SetStateAction<ModalOpenedTy>>
    name: string
}

const useModalShowStepButtonsConfigLogic = ({ myShowStepButtons, setOpened, allStepButtons, name }: ModalShowStepButtonsConfigLogicProps) => {
    const { loading, startLoading } = useIsLoading()
    const parsedStepButtons = useMemo(() => {
        const result: TwinDictionary = {}
        for (const key in allStepButtons) {
            const element = allStepButtons[key];
            result[key] = { ...element?.extraProps }
        }
        return result
    }, [allStepButtons])
    
    const [mySelectedStepButtons, setMySelectedStepButtons] = useState<VerticalSortItem[] | null>(() => {
        let mySelectedShowStepButtons = []
        let order = 0
        if (myShowStepButtons) {
            for (const element of myShowStepButtons) {
                const myButton = parsedStepButtons[element.id]
                mySelectedShowStepButtons.push({
                    id: element?.id,
                    name: myButton?.title,
                    order: order + 1
                })
                order++
            }
        }
        return mySelectedShowStepButtons
    })

    let excludeIds: string[] = []
    if (mySelectedStepButtons) {
        for (const element of mySelectedStepButtons) {
            excludeIds.push(element.id)
        }
    }

    const changeData: CustomChangeFunction = useCallback((value) => {
        const item = parsedStepButtons[value]
        setMySelectedStepButtons((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            copy.push({
                id: item?.id,
                name: item?.title,
                order: (old?.length || 0) + 1
            })
            return [...copy]
        })
    }, [setMySelectedStepButtons, parsedStepButtons])

    const onSubmitHandler = useCallback(async () => {
        startLoading()
        const mySelectedButtonsParsed = []
        if (mySelectedStepButtons) {
            for (const element of mySelectedStepButtons) {
                mySelectedButtonsParsed.push({
                    id: element.id
                })
            }
        }
        const myValuesStr = JSON.stringify(mySelectedButtonsParsed || [])
        await twinFetchPostJSON('/api/app/config/employee/updateEmployeeParameter', { parameterKey: name, value: myValuesStr })
        changeEmployeeParams({ [name]: myValuesStr })
        setOpened(null)
        return false
    }, [mySelectedStepButtons, startLoading, setOpened, name])

    return { onSubmitHandler, mySelectedStepButtons, excludeIds, changeData, loading, setMySelectedStepButtons, parsedStepButtons }
}

export default ModalShowStepButtonsConfig